/**
 * Shape Filters Section - Collection Page
 * Handles shape filter clicks, navigation, scrollbar, and dots
 * 
 * @version 1.0.0
 */
(function() {
    'use strict';

    /**
     * Initialize shape filter click handlers
     * Handles filter selection/deselection and form submission
     */
    function initShapeFilters() {
        const shapeFilters = document.querySelectorAll('[data-shape-filter]');
        const filterForm = document.querySelector('form#storefront-filters-form');
        
        if (!shapeFilters.length) return;

        shapeFilters.forEach((shapeFilter) => {
            shapeFilter.addEventListener('click', (evt) => {
                evt.preventDefault();
                const activeShape = evt.currentTarget.closest('.shape-filter-item');
                const shapeHandle = shapeFilter.getAttribute('data-shape-handle');
                const filterParam = shapeFilter.getAttribute('data-filter-param') || 'filter.p.m.filter.shape';
                const shapeInput = filterForm?.querySelector(`input[name="${filterParam}"][value="${shapeHandle}"]`);
                
                if (!shapeInput) return;

                // Toggle active state
                if (activeShape.classList.contains('active')) {
                    activeShape.classList.remove('active');
                    shapeInput.checked = false;
                } else {
                    activeShape.classList.add('active');
                    shapeInput.checked = true;
                }
                
                // Dispatch input event to trigger filter change
                filterForm.dispatchEvent(new Event('input', { bubbles: true }));
            });
        });
    }

    /**
     * Initialize navigation controls (arrows, scrollbar, dots)
     */
    function initNavigation() {
        // console.log('initNavigation');
        const scrollContainer = document.querySelector('[data-shape-scroll]');
        if (!scrollContainer) return;

        const prevBtn = document.querySelector('[data-shape-nav="prev"]');
        const nextBtn = document.querySelector('[data-shape-nav="next"]');
        const scrollbarThumb = document.querySelector('[data-shape-scrollbar-thumb]');
        const dots = document.querySelectorAll('[data-dot-index]');
        const items = document.querySelectorAll('.shape-filter-item[data-shape-index]');

        /**
         * Calculate scroll amount based on item width + gap
         * @returns {number} Scroll amount in pixels
         */
        const getScrollAmount = () => {
            const item = scrollContainer.querySelector('.shape-filter-item');
            if (!item) return 200;
            const style = window.getComputedStyle(scrollContainer);
            const gap = parseFloat(style.gap) || 15;
            return (item.offsetWidth + gap) * 3; // Scroll 3 items at a time
        };

        /**
         * Checks if there's a previous slide and/or next slide
         * And disables/enables arrows accordingly;
         * If neither previous nor next, adds is-disabled class to both
         */
        const updateArrowStates = () => {
            if (!prevBtn && !nextBtn) return;

            const scrollLeft = Math.round(scrollContainer.scrollLeft);
            const maxScrollLeft = Math.round(scrollContainer.scrollWidth - scrollContainer.clientWidth);

            const hasPrev = scrollLeft > 0;
            const hasNext = scrollLeft < maxScrollLeft && maxScrollLeft > 0;

            if (!hasPrev && !hasNext) {
                // No previous and no next: add is-disabled to both (if they exist)
                if (prevBtn) prevBtn.classList.add('is-disabled');
                if (nextBtn) nextBtn.classList.add('is-disabled');
            } else {
                // Enable/disable individually
                if (prevBtn) prevBtn.classList.toggle('is-disabled', !hasPrev);
                if (nextBtn) nextBtn.classList.toggle('is-disabled', !hasNext);
            }
        };

        // Arrow navigation - Previous
        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                scrollContainer.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
            });
        }

        // Arrow navigation - Next
        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                scrollContainer.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
            });
        }

        /**
         * Update scrollbar thumb position and width based on scroll position
         */
        const updateScrollbar = () => {
            if (!scrollbarThumb) return;
            
            const scrollWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth;
            if (scrollWidth <= 0) {
                scrollbarThumb.style.width = '100%';
                scrollbarThumb.style.marginLeft = '0';
                return;
            }
            
            const scrollPercent = scrollContainer.scrollLeft / scrollWidth;
            const thumbWidth = Math.max((scrollContainer.clientWidth / scrollContainer.scrollWidth) * 100, 20);
            const thumbPosition = scrollPercent * (100 - thumbWidth);
            
            scrollbarThumb.style.width = thumbWidth + '%';
            scrollbarThumb.style.marginLeft = thumbPosition + '%';
        };

        /**
         * Update active dot based on scroll position
         */
        const updateDots = () => {
            if (!dots.length || !items.length) return;
            
            const scrollLeft = scrollContainer.scrollLeft;
            const containerWidth = scrollContainer.clientWidth;
            let activeIndex = 0;
            
            items.forEach((item, index) => {
                const itemLeft = item.offsetLeft - scrollContainer.offsetLeft;
                if (itemLeft <= scrollLeft + containerWidth / 2) {
                    activeIndex = index;
                }
            });

            dots.forEach((dot, index) => {
                dot.classList.toggle('active', index === activeIndex);
            });
        };

        // Dot click navigation
        dots.forEach((dot) => {
            dot.addEventListener('click', () => {
                const index = parseInt(dot.getAttribute('data-dot-index'), 10);
                const targetItem = scrollContainer.querySelector(`[data-shape-index="${index}"]`);
                if (targetItem) {
                    scrollContainer.scrollTo({ 
                        left: targetItem.offsetLeft - scrollContainer.offsetLeft, 
                        behavior: 'smooth' 
                    });
                }
            });
        });

        // Throttled scroll handler for performance
        let scrollTimeout;
        scrollContainer.addEventListener('scroll', () => {
            if (scrollTimeout) return;
            scrollTimeout = setTimeout(() => {
                updateScrollbar();
                updateDots();
                updateArrowStates();
                scrollTimeout = null;
            }, 16); // ~60fps
        }, { passive: true });

        // Initial update
        updateScrollbar();
        updateDots();
        updateArrowStates();

        // Debounced resize handler
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                updateScrollbar();
                updateDots();
                updateArrowStates();
            }, 150);
        }, { passive: true });
    }

    /**
     * Initialize all functionality when DOM is ready
     */
    function init() {
        initShapeFilters();
        initNavigation();
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();

